/**
 * Tool Page Component
 * Displays a specific tool based on the URL parameter
 */
import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
} from "@fluentui/react-components";
import { Home24Regular } from "@fluentui/react-icons";

import { usePermissions } from "../auth/usePermissions";
import { useToolRegistry } from "../tools/useToolRegistry";
import { ToolLoader } from "../tools/ToolLoader";
import { AuthGuard } from "../auth/AuthGuard";
import { useUsageTracking } from "../hooks/useUsageTracking";
import type { AppRole } from "../auth/authConfig";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  breadcrumb: {
    marginBottom: tokens.spacingVerticalM,
  },
  content: {
    flex: 1,
    minHeight: 0, // Important for proper scrolling
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
  },
  notFound: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
    textAlign: "center",
  },
});

export const ToolPage: React.FC = () => {
  const styles = useStyles();
  const { toolId } = useParams<{ toolId: string }>();
  const { tools, isLoading } = useToolRegistry();
  const { hasRole } = usePermissions();
  const { trackToolAccess } = useUsageTracking();

  // Find the tool
  const tool = tools.find((t) => t.id === toolId);

  // Track tool access when tool changes
  useEffect(() => {
    if (toolId && tool) {
      trackToolAccess(toolId);
    }
  }, [toolId, tool, trackToolAccess]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
        <Text>Loading tool...</Text>
      </div>
    );
  }

  // Tool not found
  if (!tool) {
    return (
      <div className={styles.notFound}>
        <Text size={600} weight="semibold">
          Tool Not Found
        </Text>
        <Text>The tool you're looking for doesn't exist or has been removed.</Text>
      </div>
    );
  }

  // Check access
  if (tool.requiredRole && !hasRole(tool.requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <Breadcrumb className={styles.breadcrumb}>
        <BreadcrumbItem>
          <BreadcrumbButton icon={<Home24Regular />} href="/">
            Home
          </BreadcrumbButton>
        </BreadcrumbItem>
        <BreadcrumbDivider />
        {tool.category && (
          <>
            <BreadcrumbItem>
              <BreadcrumbButton>{tool.category}</BreadcrumbButton>
            </BreadcrumbItem>
            <BreadcrumbDivider />
          </>
        )}
        <BreadcrumbItem>
          <BreadcrumbButton current>{tool.name}</BreadcrumbButton>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Tool Content */}
      <div className={styles.content}>
        <AuthGuard
          requiredRoles={tool.requiredRole ? [tool.requiredRole as AppRole] : undefined}
        >
          <ToolLoader tool={tool} />
        </AuthGuard>
      </div>
    </div>
  );
};

export default ToolPage;
